import { useState } from 'react'
import { useTimeFormat, type TimeFormat } from '../../lib/timeFormat'

const MINUTES = ['00', '15', '30', '45']
const SELECT = 'min-h-11 min-w-0 flex-1 rounded-control border border-line-strong bg-canvas px-2 text-small'

type Parts = { hour: string; minute: string; period: 'AM' | 'PM' }
const EMPTY: Parts = { hour: '', minute: '', period: 'AM' }

// "HH:MM" as the parts the field shows: the hour on a 12-hour clock with its
// AM or PM, or on a 24-hour one.
function partsOf(value: string, format: TimeFormat): Parts {
  if (value === '') return EMPTY
  const [hour, minute] = value.split(':')
  if (format === '24-hour') return { hour, minute, period: 'AM' }
  const twentyFour = Number(hour)
  return { hour: String(twentyFour % 12 === 0 ? 12 : twentyFour % 12), minute, period: twentyFour >= 12 ? 'PM' : 'AM' }
}

function valueOf(parts: Parts, format: TimeFormat): string {
  const hour = Number(parts.hour)
  const twentyFour = format === '24-hour' ? hour : (hour % 12) + (parts.period === 'PM' ? 12 : 0)
  return `${String(twentyFour).padStart(2, '0')}:${parts.minute}`
}

/**
 * A time chosen from lists whose minutes are 00, 15, 30, and 45, in the clock
 * the agent reads times in (24-hour, or 12-hour with AM and PM). The value is
 * "HH:MM", or empty until the hour and the minute are both chosen. A time saved
 * with another minute stays a choice, so reopening a request does not change it.
 */
export default function QuarterHourField({ label, id, value, onChange }: { label: string; id: string; value: string; onChange: (value: string) => void }) {
  const { format } = useTimeFormat()
  const [draft, setDraft] = useState<Parts>(EMPTY)
  const parts = value === '' ? draft : partsOf(value, format)
  const hours = format === '24-hour' ? Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0')) : ['12', ...Array.from({ length: 11 }, (_, hour) => String(hour + 1))]
  const minutes = parts.minute !== '' && !MINUTES.includes(parts.minute) ? [parts.minute, ...MINUTES] : MINUTES

  function change(field: keyof Parts, next: string) {
    const updated = { ...parts, [field]: next } as Parts
    if (updated.hour !== '' && updated.minute !== '') {
      onChange(valueOf(updated, format))
      return
    }
    setDraft(updated)
    onChange('')
  }

  return (
    <div role="group" aria-label={label} id={id} className="flex gap-1.5">
      <select aria-label={`${label} hour`} value={parts.hour} onChange={(event) => change('hour', event.target.value)} className={SELECT}>
        <option value="">--</option>
        {hours.map((hour) => (
          <option key={hour} value={hour}>
            {hour}
          </option>
        ))}
      </select>
      <select aria-label={`${label} minutes`} value={parts.minute} onChange={(event) => change('minute', event.target.value)} className={SELECT}>
        <option value="">--</option>
        {minutes.map((minute) => (
          <option key={minute} value={minute}>
            {minute}
          </option>
        ))}
      </select>
      {format === '12-hour' && (
        <select aria-label={`${label} AM or PM`} value={parts.period} onChange={(event) => change('period', event.target.value)} className={SELECT}>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      )}
    </div>
  )
}
